import {
  CHAT_MAX_CHARS,
  CLIENT_MESSAGES,
  CLOSE_CODES,
  ChatMessage,
  ChatRejected,
  ChatRemoved,
  ControlChanged,
  ModerationNotice,
  DmNotice,
  PropertyChanged,
  RoomWelcome,
  SERVER_MESSAGES,
  SeatResult,
  ServiceDraining,
  TransitionFailed,
  TransitionReady,
  VehicleResult,
  VenueEventMessage,
  VenuePrivateMessage,
  VenueStateMessage,
  WorldSwitchRefused,
  parseCopyOption,
  streetCopyOf,
  withProtocolVersion,
  type Appearance as AppearanceT,
  type InputDrive,
  type InputMove,
  type RoomTarget,
  type RoomTicketResponse,
  type SeatAction,
  type Vec2,
  type VehicleAction,
  type VenueActionMessage,
  type ViewFocus,
  type WorldSwitch,
} from "@townsy/contracts";
import { z } from "zod";
import { Emitter } from "../lib/emitter";
import type { ActorPostureVisual, ActorVisual } from "../scene/types";
import { appearanceFromState, appearanceKey } from "./appearance";
import { describeClose, describeJoinError, type CloseInfo, type JoinFailure } from "./closeCodes";
import type { ActorStateLike, Connector, RoomHandle, VehicleStateLike } from "./connector";
import type { AuthoritativeState } from "./prediction";
import { toVehicleSnapshot, type VehicleSnapshot } from "./vehicles";

/**
 * `room.transition_ready` with an optional human-readable reason for a server-initiated move (a home
 * made private moves its visitors to the street). Unknown fields are otherwise stripped; the text is
 * untrusted plain text, length-limited and never rendered as markup.
 */
const TransitionReadyWithNotice = TransitionReady.extend({
  // Lenient on purpose: a malformed reason must never make the client ignore the move itself.
  notice: z.unknown().optional(),
  message: z.unknown().optional(),
});

function noticeText(...candidates: unknown[]): string {
  for (const c of candidates) if (typeof c === "string" && c.trim()) return c.trim().slice(0, 300);
  return "";
}

/**
 * Realtime session for the town (brief section 5, docs/03 sections 4-5, ADR-0002).
 *
 *  - Joins are always ticket-based: `POST /room-tickets` (overview Enter / first join) or a
 *    `room.transition_ready` ticket (walk-mode Enter / Exit). The ticket is the SDK auth token.
 *  - Transitions are atomic from the client's point of view: the destination is joined first;
 *    only on success is the origin left (consented) and the scene swapped. On failure the
 *    origin membership is kept (or restored when the server already closed it).
 *  - A reconnection token is kept in session storage so a reload tries `client.reconnect` first
 *    and keeps the same actor without counting a new room entry (T04).
 *  - Close codes map to explicit UI states (`describeClose`); nothing here grants anything.
 */
export type RoomKind = "street" | "interior";
export type ConnectionStatus = "idle" | "connecting" | "joined" | "reconnecting" | "transitioning" | "closed";

/** What a `room.transition_ready` says about the move it starts (RealtimeEvents.transitionStarted). */
export interface TransitionStartedInfo {
  roomName: "street" | "interior";
  propertyId: string | null;
  /** The district named by a street ticket (the square or door it lands at; null for an interior). */
  districtId: string | null;
  serverInitiated: boolean;
  notice?: string;
  /** ADR-0043: the world copy the ticket names (a street's `copy`; an interior's `fromCopy`); absent when it names none. */
  worldCopy?: number;
}

export interface RoomContext {
  kind: RoomKind;
  roomName: string;
  roomKey: string | null;
  districtId: string | null;
  propertyId: string | null;
  presentationVersion: number | null;
  sessionId: string;
  yourActorId: string;
  simulationHz: number;
  chatDwellSeconds: number;
  chatMinIntervalMs: number;
  joinedAt: number;
  /** True when this membership was restored through a reconnection token (not a new entry). */
  restored: boolean;
  /**
   * ADR-0043: the world copy (1 = Robin): on the street the room's copy, inside a building the copy the visitor came
   * from (from the welcome; the street's room key names it too). Null before the welcome or when unknown.
   */
  worldCopy: number | null;
}

export interface ReconnectRecord {
  token: string;
  actorId: string;
  roomName: string;
  kind: RoomKind;
  districtId: string | null;
  propertyId: string | null;
  savedAt: number;
}

export interface ReconnectStorage {
  load(): ReconnectRecord | null;
  save(record: ReconnectRecord): void;
  clear(): void;
}

export interface RealtimeEvents {
  status(status: ConnectionStatus, context: RoomContext | null): void;
  /** The current room closed on us (not a consented leave, not a transition). */
  closed(info: CloseInfo): void;
  /** A join or transition could not be completed; the previous membership (if any) is unchanged. */
  joinFailed(failure: JoinFailure): void;
  actorUpsert(actor: ActorVisual): void;
  /**
   * An actor left this tab's room state: they left the room, or (street, ADR-0043) walked out of this tab's view. The
   * client cannot tell which, so nothing may announce a departure from it.
   */
  actorRemove(actorId: string): void;
  /** ADR-0043: the current room's people (visitors, AI residents excluded), from the room state's `peopleCount`. */
  peopleCount(count: number): void;
  localActor(actorId: string | null): void;
  /** Authoritative state of the local actor (for prediction/reconciliation). */
  localState(state: AuthoritativeState): void;
  welcome(welcome: RoomWelcome): void;
  chatMessage(message: ChatMessage): void;
  chatRejected(rejection: ChatRejected): void;
  chatRemoved(id: string): void;
  moderation(notice: ModerationNotice): void;
  /** ADR-0014: a new private message for this visitor (ids only; read it from the API). */
  dmNotice(notice: DmNotice): void;
  propertyChanged(change: PropertyChanged): void;
  /** Private invalidation: this visitor's balances changed; refetch `GET /me/wallets` (never trust a payload). */
  walletChanged(): void;
  /**
   * A transition ticket arrived. `serverInitiated` is true when the client had not asked for one
   * (walk-through doors: the server detects the avatar pushing into a door and sends the ticket).
   * `districtId` is the district a street ticket names (null for an interior; town v6: the whole town is one street).
   */
  transitionStarted(info: TransitionStartedInfo): void;
  transitionFailed(failure: TransitionFailed): void;
  draining(notice: ServiceDraining): void;
  degraded(message: string): void;
  controlChanged(change: ControlChanged): void;
  dropped(code: number): void;
  reconnected(): void;
  /** A shared town car appeared or changed (street rooms; ADR-0006). Includes the car the local player drives. */
  vehicleUpsert(vehicle: VehicleSnapshot): void;
  /** A car left the room state (returned to traffic elsewhere, or the room was left). */
  vehicleRemove(vehicleId: string): void;
  /** Answer to this client's `vehicle.action` (sent to the requester only). */
  vehicleResult(result: VehicleResult): void;
  /** Answer to this client's sit / lie / stand (ADR-0018; sent to the requester only, after the state shows it). */
  seatResult(result: SeatResult): void;
  /** Attractions (ADR-0016): the venue game's shared state, this visitor's own state, and one-off notices. */
  venueState(message: VenueStateMessage): void;
  venuePrivate(message: VenuePrivateMessage): void;
  venueEvent(message: VenueEventMessage): void;
  /** ADR-0043: the room refused this client's `world.switch` (the visitor stays where they are). */
  worldSwitchRefused(refusal: WorldSwitchRefused): void;
}

export interface RealtimeDeps {
  connector: Connector;
  requestTicket(target: RoomTarget): Promise<RoomTicketResponse>;
  storage: ReconnectStorage;
  /** The visitor's public actor id from bootstrap; reconnection records for other actors are ignored. */
  actorId: string;
  defaults: { simulationHz: number; chatDwellSeconds: number; chatMinIntervalMs: number };
  now?: () => number;
  /** Timer used for automatic re-joins (injectable for tests). */
  schedule?: (fn: () => void, ms: number) => void;
  /** Runs `fn` once the browser has a network connection (now, or at its next `online` event); injectable for tests. */
  whenOnline?: (fn: () => void) => void;
  log?: (message: string, ...args: unknown[]) => void;
  /**
   * Interiors whose room has vehicles to drive (ADR-0016: the Monster Truck Arena). Driving messages go only to the
   * street and to these rooms; every other building refuses them locally.
   */
  drivableInterior?: (propertyId: string) => boolean;
  /**
   * The current room's seat by id (ADR-0018: the shared seat list the session keeps for the room), for drawing a
   * seated actor at the seat's height. Unknown seats fall back to a typical chair / bed height.
   */
  resolveSeat?: (seatId: string) => { kind: "sit" | "lie"; y: number } | null;
}

/** Drawn seat height when the seat is not (yet) in this client's list: a chair, or a bed. */
const FALLBACK_SEAT_Y = { sit: 0.47, lie: 0.62 } as const;

interface Binding {
  id: number;
  handle: RoomHandle;
  context: RoomContext;
  actors: Map<string, ActorVisual>;
  /** Car each actor (by session id) is driving; absent / "" while walking. */
  actorVehicles: Map<string, string>;
  /** Shared town cars by vehicleId (validated copies of the room state). */
  vehicles: Map<string, VehicleSnapshot>;
  appearanceCache: Map<string, { key: string; appearance: AppearanceT }>;
  /** A transition away from this room is in progress; its close must not surface in the UI. */
  retiring: boolean;
  closed: boolean;
  /** Latest authoritative state of the local actor in this room (re-emitted if a transition away fails). */
  lastLocalState: AuthoritativeState | null;
  /** ADR-0043: the room's people count from its state (`peopleCount`), or null before the first state. */
  peopleCount: number | null;
  detach: Array<() => void>;
}

const AUTO_REJOIN_LIMIT = 3;
const AUTO_REJOIN_WINDOW_MS = 60_000;
/**
 * A failed automatic re-join (the network is still down although the browser reports it online) tries again this much
 * later, within the cap above: three tries cover ~20 s before the banner's Rejoin is the way back.
 */
const AUTO_REJOIN_RETRY_MS = 10_000;
/** ADR-0053: `chat.typing` goes out at most this often while the visitor types (a resident waits 20 s from each one). */
export const CHAT_TYPING_SEND_MS = 2000;

function browserWhenOnline(fn: () => void): void {
  if (typeof window === "undefined" || typeof navigator === "undefined" || navigator.onLine !== false) {
    fn();
    return;
  }
  window.addEventListener("online", () => fn(), { once: true });
}

/** A street target without the one-shot ROOM_FULL retry hint (what is remembered for later re-joins). */
function withoutAvoid(target: RoomTarget): RoomTarget {
  if (target.kind !== "street" || target.avoidCopy === undefined) return target;
  return target.districtId ? { kind: "street", districtId: target.districtId } : { kind: "street" };
}

export function targetFromContext(ctx: Pick<RoomContext, "kind" | "districtId" | "propertyId">): RoomTarget | null {
  // Town v6: the one street room; a known district only picks the square a fresh join lands on.
  if (ctx.kind === "street") return ctx.districtId ? { kind: "street", districtId: ctx.districtId } : { kind: "street" };
  if (ctx.kind === "interior" && ctx.propertyId) return { kind: "interior", propertyId: ctx.propertyId };
  return null;
}

export class RealtimeClient {
  readonly events = new Emitter<RealtimeEvents>();
  private binding: Binding | null = null;
  private status: ConnectionStatus = "idle";
  private transitionInFlight = false;
  /** An `interaction` enter/exit was sent and its `room.transition_ready` has not arrived yet. */
  private interactionPending = false;
  /** ADR-0043: a `world.switch` was sent and neither its ticket nor its refusal has arrived yet. */
  private switchPending = false;
  /** The last join / transition failure (diagnostics: `lastJoinFailure`). */
  private lastFailure: JoinFailure | null = null;

  /** The last join or transition failure of this client (diagnostics and tests), or null after a success. */
  get lastJoinFailure(): JoinFailure | null {
    return this.lastFailure;
  }
  private lastTarget: RoomTarget | null = null;
  private bindingSeq = 0;
  private autoRejoins: number[] = [];
  private lastTypingSentAt = Number.NEGATIVE_INFINITY;
  private disposed = false;
  private readonly now: () => number;
  private readonly schedule: (fn: () => void, ms: number) => void;
  private readonly whenOnline: (fn: () => void) => void;
  private readonly log: (message: string, ...args: unknown[]) => void;

  constructor(private readonly deps: RealtimeDeps) {
    this.now = deps.now ?? (() => Date.now());
    this.schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms));
    this.whenOnline = deps.whenOnline ?? browserWhenOnline;
    this.log = deps.log ?? ((m, ...a) => console.info(`[realtime] ${m}`, ...a));
  }

  get connectionStatus(): ConnectionStatus {
    return this.status;
  }

  get context(): RoomContext | null {
    return this.binding?.context ?? null;
  }

  get currentTarget(): RoomTarget | null {
    return this.lastTarget;
  }

  get isTransitioning(): boolean {
    return this.transitionInFlight;
  }

  /** Colyseus room id of the current membership (diagnostics and the local-only test hook). */
  get roomId(): string | null {
    return this.binding && !this.binding.closed ? this.binding.handle.roomId : null;
  }

  getActors(): ActorVisual[] {
    return this.binding ? Array.from(this.binding.actors.values()) : [];
  }

  /** Shared town cars of the current room (latest server state). */
  getVehicles(): VehicleSnapshot[] {
    return this.binding ? Array.from(this.binding.vehicles.values()) : [];
  }

  /** Positions of the OTHER actors who are on foot (the walkers a driven car must not touch). */
  getWalkers(): Vec2[] {
    const b = this.binding;
    if (!b) return [];
    const out: Vec2[] = [];
    for (const [sessionId, a] of b.actors) {
      if (a.isLocal || b.actorVehicles.get(sessionId)) continue;
      out.push({ x: a.x, z: a.z });
    }
    return out;
  }

  // ---------------------------------------------------------------- joins

  /** First join / return to the street. Tries the saved reconnection token first on a fresh load. */
  async joinStreet(districtId?: string, opts: { reconnectFirst?: boolean } = {}): Promise<boolean> {
    // Town v6: one street room; `districtId` picks the square a fresh join lands on. Already on the street: nothing to do.
    const target: RoomTarget = districtId ? { kind: "street", districtId } : { kind: "street" };
    if (this.binding && !this.binding.closed && sameTarget(targetFromContext(this.binding.context), target)) return true;
    if (!this.binding && (opts.reconnectFirst ?? true) && (await this.tryReconnect())) return true;
    return this.joinTarget(target);
  }

  /** Overview "Enter": explicit fast-travel authorised by the API (docs/03 section 4). */
  async joinInterior(propertyId: string): Promise<boolean> {
    return this.joinTarget({ kind: "interior", propertyId });
  }

  /** Re-join the room we were last in (after a close, or "Take control here"). */
  async rejoin(): Promise<boolean> {
    if (!this.lastTarget) return false;
    return this.joinTarget(this.lastTarget);
  }

  takeControlHere(): Promise<boolean> {
    return this.rejoin();
  }

  /**
   * Ask the API for a ticket and join. ADR-0043: the API placed a street ticket in a world copy; when two arrivals race
   * for its last place the copy refuses one with ROOM_FULL, and that one asks ONCE more with `avoidCopy` (the API then
   * places it in another copy, opening one if needed) before any "room full" shows. ROOM_FULL never strands a visitor:
   * a second refusal shows the retryable room-full state.
   */
  private async joinTarget(target: RoomTarget, retried = false): Promise<boolean> {
    if (this.transitionInFlight) return false;
    const quiet = target.kind === "street" && !retried && target.avoidCopy === undefined;
    this.quietRefusal = false;
    const ok = await this.joinTargetOnce(target, quiet);
    if (ok || !this.takeQuietRefusal() || this.disposed) return ok;
    const copy = this.lastRefusedCopy;
    this.log(`world copy ${copy ?? "?"} was full; one retry in another copy`);
    return this.joinTarget(copy !== null && target.kind === "street" ? { ...target, avoidCopy: copy } : target, true);
  }

  /** The copy named by the street ticket that a quiet ROOM_FULL refused (for the retry's `avoidCopy`). */
  private lastRefusedCopy: number | null = null;
  /** A street join was refused with ROOM_FULL without telling anyone: the caller owes it one retry. */
  private quietRefusal = false;

  private takeQuietRefusal(): boolean {
    const was = this.quietRefusal;
    this.quietRefusal = false;
    return was;
  }

  private async joinTargetOnce(target: RoomTarget, quietRoomFull: boolean): Promise<boolean> {
    this.lastTarget = withoutAvoid(target);
    let ticket: RoomTicketResponse;
    if (!this.binding) this.setStatus("connecting");
    try {
      this.transitionInFlight = true;
      ticket = await this.deps.requestTicket(target);
    } catch (err) {
      this.transitionInFlight = false;
      const failure: JoinFailure = {
        state: "join_failed",
        message: errorText(err) || "Couldn't get a room pass.",
        retryable: isRetryable(err),
      };
      this.lastFailure = failure;
      const origin = this.binding;
      if (origin && !origin.closed && origin.retiring) {
        // ADR-0043: the ROOM_FULL retry's ticket request failed: the origin of the move is active again.
        origin.retiring = false;
        if (origin.lastLocalState) this.events.emit("localState", origin.lastLocalState);
      }
      if (this.binding && !this.binding.closed) this.setStatus("joined");
      else this.setStatus(this.binding ? "closed" : "idle");
      this.events.emit("joinFailed", failure);
      return false;
    }
    this.transitionInFlight = false;
    return this.transitionTo(ticket, target, { quietRoomFull });
  }

  private async tryReconnect(): Promise<boolean> {
    const record = this.deps.storage.load();
    if (!record || record.actorId !== this.deps.actorId || !record.token) {
      this.log("no usable reconnection record", record ? `actor ${record.actorId} vs ${this.deps.actorId}, token ${record.token ? "set" : "missing"}` : "none saved");
      return false;
    }
    const target = targetFromContext(record);
    if (!target) {
      this.log("reconnection record has no target; discarding it");
      this.deps.storage.clear();
      return false;
    }
    this.setStatus("connecting");
    try {
      const handle = await this.deps.connector.reconnect(record.token);
      this.lastTarget = target;
      const binding = this.bind(handle, target, record.roomName, true);
      this.binding = binding;
      this.activate(binding);
      this.persist(binding);
      this.setStatus("joined");
      this.log("restored room membership through reconnection token");
      return true;
    } catch (err) {
      if (isServerRejection(err)) {
        // The server answered (token expired, room gone, access refused): the record is useless now.
        this.deps.storage.clear();
        this.log("reconnection token rejected; joining with a new ticket", errorText(err));
      } else {
        // No server answer (request aborted by a navigation, network blip): keep the record so the
        // next load can still restore the membership within the server's reconnection grace.
        this.log("reconnection attempt did not reach the server; keeping the record", errorText(err));
      }
      return false;
    }
  }

  /**
   * Join `ticket.roomName` and only then leave the current room. Duplicate requests while a
   * transition is in flight are ignored (the UI disables Enter meanwhile).
   */
  private async transitionTo(ticket: RoomTicketResponse, target: RoomTarget, opts: { quietRoomFull?: boolean } = {}): Promise<boolean> {
    if (this.transitionInFlight || this.disposed) return false;
    this.transitionInFlight = true;
    this.lastFailure = null;
    const old = this.binding;
    if (old) {
      old.retiring = true;
      this.setStatus("transitioning", old.context);
    } else if (this.status !== "connecting") {
      this.setStatus("connecting");
    }
    let handle: RoomHandle;
    try {
      // Every join carries this build's room protocol version (ADR-0036); the server refuses outdated tabs.
      handle = await this.deps.connector.joinWithTicket(ticket.roomName, ticket.ticket, withProtocolVersion(ticket.joinOptions));
    } catch (err) {
      this.transitionInFlight = false;
      const failure = describeJoinError(err);
      this.lastFailure = failure;
      if (opts.quietRoomFull && failure.state === "room_full" && ticket.roomName === "street" && (!old || !old.closed)) {
        // ADR-0043: the copy this ticket named filled up meanwhile; the caller retries once in another copy before
        // anything shows. The move is still under way: the origin (if any) stays the retiring origin of the retry, which
        // restores it on its own failure exactly as on any refusal. (An origin that closed meanwhile is restored below.)
        this.lastRefusedCopy = parseCopyOption(ticket.joinOptions["copy"]);
        this.quietRefusal = true;
        return false;
      }
      if (failure.state === "incompatible_client") {
        // This tab is older (or newer) than the realtime server: no retry or origin restore can succeed, only a
        // reload. The origin room (if still open) is kept so the visitor is not dropped mid-sentence.
        if (old && !old.closed) {
          old.retiring = false;
          this.setStatus("joined", old.context);
          if (old.lastLocalState) this.events.emit("localState", old.lastLocalState);
        } else {
          this.binding = null;
          this.setStatus("closed");
        }
        this.events.emit("joinFailed", failure);
        if (!this.binding) this.events.emit("closed", describeClose(CLOSE_CODES.INCOMPATIBLE_CLIENT));
        return false;
      }
      if (old && !old.closed) {
        // Keep the origin membership and tell the UI why the destination was refused.
        old.retiring = false;
        this.setStatus("joined", old.context);
        this.events.emit("joinFailed", failure);
        // Prediction was discarded when the transition started: re-seed it from the origin room.
        if (old.lastLocalState) this.events.emit("localState", old.lastLocalState);
        return false;
      }
      this.events.emit("joinFailed", failure);
      if (old && old.closed) {
        // The server already closed the origin while we were moving: restore it with a new ticket.
        this.binding = null;
        const origin = targetFromContext(old.context);
        this.setStatus("closed");
        if (origin) {
          this.lastTarget = origin;
          const restored = await this.joinTarget(origin);
          if (!restored) this.events.emit("closed", describeClose(4003));
          return false;
        }
      }
      this.setStatus(this.binding ? "joined" : "idle");
      if (!this.binding) this.events.emit("closed", { ...describeClose(4003), message: failure.message, retryable: failure.retryable });
      return false;
    }

    const binding = this.bind(handle, target, ticket.roomName, false);
    binding.context = { ...binding.context, worldCopy: worldCopyOfTicket(ticket) };
    this.binding = binding;
    this.interactionPending = false;
    this.activate(binding);
    this.lastTarget = withoutAvoid(target);
    if (old) {
      if (!old.closed) {
        old.closed = true;
        for (const d of old.detach) d();
        old.handle.leave(true).catch(() => {});
      }
      this.retireActors(old, binding);
    }
    this.persist(binding);
    this.transitionInFlight = false;
    this.setStatus("joined", binding.context);
    return true;
  }

  // ---------------------------------------------------------------- binding

  private bind(handle: RoomHandle, target: RoomTarget, roomName: string, restored: boolean): Binding {
    const d = this.deps.defaults;
    const binding: Binding = {
      id: ++this.bindingSeq,
      handle,
      context: {
        kind: target.kind,
        roomName,
        roomKey: null,
        districtId: target.kind === "street" ? (target.districtId ?? null) : null,
        propertyId: target.kind === "interior" ? target.propertyId : null,
        presentationVersion: null,
        sessionId: handle.sessionId,
        yourActorId: this.deps.actorId,
        simulationHz: d.simulationHz,
        chatDwellSeconds: d.chatDwellSeconds,
        chatMinIntervalMs: d.chatMinIntervalMs,
        joinedAt: this.now(),
        restored,
        worldCopy: null,
      },
      actors: new Map(),
      actorVehicles: new Map(),
      vehicles: new Map(),
      appearanceCache: new Map(),
      retiring: false,
      closed: false,
      lastLocalState: null,
      peopleCount: null,
      detach: [],
    };
    const on = <T>(type: string, schema: { safeParse(v: unknown): { success: boolean; data?: T } }, cb: (payload: T) => void) => {
      binding.detach.push(
        handle.onMessage(type, (payload) => {
          const parsed = schema.safeParse(payload);
          if (!parsed.success || parsed.data === undefined) {
            this.log(`ignored malformed ${type} message`);
            return;
          }
          cb(parsed.data);
        }),
      );
    };

    on(SERVER_MESSAGES.welcome, RoomWelcome, (w) => {
      binding.context = {
        ...binding.context,
        kind: w.roomKind,
        roomKey: w.roomKey,
        districtId: w.districtId ?? binding.context.districtId,
        propertyId: w.propertyId ?? binding.context.propertyId,
        presentationVersion: w.presentationVersion ?? binding.context.presentationVersion,
        // ADR-0043: the copy (street: the room's own; inside: the copy the visitor came from).
        worldCopy: w.worldCopy ?? (w.roomKind === "street" ? streetCopyOf(w.roomKey) : null) ?? binding.context.worldCopy,
        yourActorId: w.yourActorId,
        simulationHz: w.simulationHz,
        chatDwellSeconds: w.chatDwellSeconds,
        chatMinIntervalMs: w.chatMinIntervalMs,
      };
      this.events.emit("welcome", w);
      if (this.binding === binding) this.setStatus(this.status === "joined" ? "joined" : this.status, binding.context);
    });
    on(SERVER_MESSAGES.controlChanged, ControlChanged, (c) => this.events.emit("controlChanged", c));
    // Both the answer to an Enter/Exit `interaction` AND a server-initiated walk-through (the avatar
    // pushed into a door; ADR-0004 #2) arrive here. There may be no pending request: the ticket is
    // the server's decision, so the client simply runs the same atomic join-then-leave.
    on(SERVER_MESSAGES.transitionReady, TransitionReadyWithNotice, (t) => {
      if (this.binding !== binding || binding.closed || binding.retiring) return;
      const propertyId = t.propertyId ?? t.joinOptions["propertyId"] ?? "";
      // Town v6 (ADR-0042, protocol 3): one street room; a street ticket's district is informational (the district of
      // the door an exit leaves by, or the square it lands at) and may be absent.
      const districtId = t.districtId ?? t.joinOptions["spawnDistrictId"] ?? "";
      if (t.roomName === "interior" && !propertyId) {
        this.log("ignored room.transition_ready without a propertyId");
        return;
      }
      if (this.transitionInFlight) {
        this.log("ignored room.transition_ready while another transition is in flight");
        return;
      }
      const serverInitiated = !this.interactionPending;
      this.interactionPending = false;
      // ADR-0043: the answer to this client's `world.switch` (a refusal of its join must stay a refusal, never a move).
      const switching = this.switchPending;
      this.switchPending = false;
      const next: RoomTarget = t.roomName === "interior" ? { kind: "interior", propertyId } : districtId ? { kind: "street", districtId } : { kind: "street" };
      // A server move can explain itself (e.g. "This home is now private"): plain text, shown as a toast.
      const notice = serverInitiated ? noticeText(t.notice, t.message) : "";
      // ADR-0043: the copy the ticket names (absent for a ticket from before named world copies).
      const ticketCopy = parseCopyOption(t.joinOptions[t.roomName === "street" ? "copy" : "fromCopy"]);
      this.events.emit("transitionStarted", {
        roomName: t.roomName,
        propertyId: t.roomName === "interior" ? propertyId : null,
        districtId: t.roomName === "street" ? districtId || null : null,
        serverInitiated,
        ...(notice ? { notice } : {}),
        ...(ticketCopy !== null ? { worldCopy: ticketCopy } : {}),
      });
      const quietRoomFull = t.roomName === "street" && !switching;
      this.quietRefusal = false;
      void this.transitionTo({ ticket: t.ticket, roomName: t.roomName, joinOptions: t.joinOptions, expiresAt: "" }, next, { quietRoomFull }).then((ok) => {
        if (ok || !this.takeQuietRefusal() || this.disposed) return;
        // ADR-0043: the copy the room picked for this exit filled up meanwhile: one placement through the API (another
        // copy), on the square of the door's district. The building stays the origin until that join succeeds.
        const copy = parseCopyOption(t.joinOptions["copy"]);
        void this.joinTarget({ kind: "street", ...(districtId ? { districtId } : {}), ...(copy !== null ? { avoidCopy: copy } : {}) }, true);
      });
    });
    on(SERVER_MESSAGES.transitionFailed, TransitionFailed, (f) => {
      if (this.binding !== binding) return;
      this.interactionPending = false;
      this.events.emit("transitionFailed", f);
    });
    on(SERVER_MESSAGES.worldSwitchRefused, WorldSwitchRefused, (r) => {
      if (this.binding !== binding || binding.closed) return;
      this.switchPending = false;
      this.interactionPending = false;
      this.events.emit("worldSwitchRefused", r);
    });
    on(SERVER_MESSAGES.chatMessage, ChatMessage, (m) => this.events.emit("chatMessage", m));
    on(SERVER_MESSAGES.chatRejected, ChatRejected, (r) => this.events.emit("chatRejected", r));
    on(SERVER_MESSAGES.chatRemoved, ChatRemoved, (r) => this.events.emit("chatRemoved", r.id));
    on(SERVER_MESSAGES.moderationNotice, ModerationNotice, (n) => this.events.emit("moderation", n));
    on(SERVER_MESSAGES.dmNotice, DmNotice, (n) => this.events.emit("dmNotice", n));
    on(SERVER_MESSAGES.propertyChanged, PropertyChanged, (p) => {
      if (p.propertyId === binding.context.propertyId && p.presentationVersion !== undefined) {
        binding.context = { ...binding.context, presentationVersion: p.presentationVersion };
      }
      this.events.emit("propertyChanged", p);
    });
    on(SERVER_MESSAGES.serviceDraining, ServiceDraining, (s) => this.events.emit("draining", s));
    on(SERVER_MESSAGES.vehicleResult, VehicleResult, (r) => {
      if (this.binding === binding && !binding.closed) this.events.emit("vehicleResult", r);
    });
    on(SERVER_MESSAGES.seatResult, SeatResult, (r) => {
      if (this.binding === binding && !binding.closed) this.events.emit("seatResult", r);
    });
    on(SERVER_MESSAGES.venueState, VenueStateMessage, (m) => {
      if (this.binding === binding && !binding.closed) this.events.emit("venueState", m);
    });
    on(SERVER_MESSAGES.venuePrivate, VenuePrivateMessage, (m) => {
      if (this.binding === binding && !binding.closed) this.events.emit("venuePrivate", m);
    });
    on(SERVER_MESSAGES.venueEvent, VenueEventMessage, (m) => {
      if (this.binding === binding && !binding.closed) this.events.emit("venueEvent", m);
    });
    binding.detach.push(
      handle.onMessage(SERVER_MESSAGES.serviceDegraded, (payload) => {
        const message = payload && typeof payload === "object" && "message" in payload ? String((payload as { message: unknown }).message) : "";
        this.events.emit("degraded", message || "Some town features are temporarily degraded.");
      }),
    );
    // Private, invalidation-only signals: the payload is never trusted, the UI refetches from the API.
    binding.detach.push(
      handle.onMessage(SERVER_MESSAGES.walletChanged, () => {
        if (this.binding === binding) this.events.emit("walletChanged");
      }),
    );
    binding.detach.push(handle.onMessage(SERVER_MESSAGES.notificationCreated, () => this.log("notification.created (ignored in M1)")));

    try {
      binding.detach.push(
        handle.listenRoomField("presentationVersion", (value) => {
          if (typeof value === "number") binding.context = { ...binding.context, presentationVersion: value };
        }),
      );
    } catch {
      /* optional field */
    }

    binding.detach.push(
      handle.onLeave((code, reason) => this.onLeave(binding, code, reason)),
      handle.onError((code, message) => this.log(`room error ${code}: ${message ?? ""}`)),
      handle.onDrop((code) => {
        if (this.binding !== binding || binding.closed) return;
        this.setStatus("reconnecting", binding.context);
        this.events.emit("dropped", code);
      }),
      handle.onReconnect(() => {
        if (this.binding !== binding || binding.closed) return;
        this.persist(binding);
        this.setStatus("joined", binding.context);
        this.events.emit("reconnected");
      }),
    );
    return binding;
  }

  /**
   * Wire the `actors` collection. Called only after `this.binding` points at `binding`, because
   * the schema callbacks report already-present actors synchronously during registration.
   */
  private activate(binding: Binding): void {
    // ADR-0043: the room's own people count (the street sends this tab only the actors near it, so they are not counted).
    try {
      binding.detach.push(
        binding.handle.listenRoomField("peopleCount", (value) => {
          if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return;
          binding.peopleCount = Math.floor(value);
          if (this.binding === binding && !binding.closed) this.events.emit("peopleCount", binding.peopleCount);
        }),
      );
    } catch {
      /* optional field */
    }
    binding.detach.push(
      binding.handle.bindActors({
        onAdd: (actor, sessionId) => this.onActor(binding, actor, sessionId, true),
        onChange: (actor, sessionId) => this.onActor(binding, actor, sessionId, false),
        onRemove: (_actor, sessionId) => {
          const visual = binding.actors.get(sessionId);
          binding.actors.delete(sessionId);
          binding.actorVehicles.delete(sessionId);
          binding.appearanceCache.delete(sessionId);
          if (!visual || this.binding !== binding) return;
          if (visual.isLocal) this.events.emit("localActor", null);
          this.events.emit("actorRemove", visual.actorId);
        },
      }),
      binding.handle.bindVehicles({
        onAdd: (vehicle, key) => this.onVehicle(binding, vehicle, key),
        onChange: (vehicle, key) => this.onVehicle(binding, vehicle, key),
        onRemove: (vehicle, key) => {
          const id = (vehicle && typeof vehicle.vehicleId === "string" && vehicle.vehicleId) || key;
          if (!binding.vehicles.delete(id) || this.binding !== binding) return;
          this.events.emit("vehicleRemove", id);
        },
      }),
    );
  }

  private onVehicle(binding: Binding, raw: VehicleStateLike, key: string): void {
    const snapshot = toVehicleSnapshot(raw, key);
    if (!snapshot) {
      this.log("ignored a car without an id");
      return;
    }
    binding.vehicles.set(snapshot.vehicleId, snapshot);
    if (this.binding === binding) this.events.emit("vehicleUpsert", snapshot);
  }

  private onActor(binding: Binding, actor: ActorStateLike, sessionId: string, added: boolean): void {
    const isLocal = sessionId === binding.handle.sessionId;
    const previous = binding.actors.get(sessionId);
    const visual = this.toVisual(binding, actor, sessionId, isLocal);
    binding.actors.set(sessionId, visual);
    const vehicleId = typeof actor.vehicleId === "string" ? actor.vehicleId : "";
    binding.actorVehicles.set(sessionId, vehicleId);
    if (this.binding !== binding) return;
    if (isLocal) {
      if (added) {
        this.events.emit("actorUpsert", visual);
        this.events.emit("localActor", visual.actorId);
      } else if (previous && (previous.appearance !== visual.appearance || previous.label !== visual.label || previous.countryCode !== visual.countryCode)) {
        // Presentation of our own avatar changed (e.g. "Edit avatar" saved): re-skin it; the
        // position keeps coming from prediction via `localState`.
        this.events.emit("actorUpsert", visual);
      }
      const state: AuthoritativeState = {
        x: actor.x,
        z: actor.z,
        facing: actor.facing,
        anim: animOf(actor.anim),
        lastSeq: Number.isFinite(actor.lastSeq) ? actor.lastSeq : 0,
        vehicleId,
      };
      // Sitting / lying (ADR-0018); a standing actor's state has neither field.
      const posture = postureCode(actor.posture);
      if (posture !== 0) {
        state.posture = posture;
        state.seatId = typeof actor.seatId === "string" ? actor.seatId : "";
      }
      binding.lastLocalState = state;
      // The origin room of a transition must not seed prediction: the session discards its predictor
      // when the transition starts, and a late origin patch would recreate it with the OLD room's
      // position, colliders and input sequence (the destination's first state, lastSeq 0, would then
      // be dropped as stale). The destination's first state seeds the new membership instead.
      if (binding.retiring) return;
      this.events.emit("localState", state);
      return;
    }
    this.events.emit("actorUpsert", visual);
  }

  private toVisual(binding: Binding, a: ActorStateLike, sessionId: string, isLocal: boolean): ActorVisual {
    const key = appearanceKey(a);
    let cached = binding.appearanceCache.get(sessionId);
    if (!cached || cached.key !== key) {
      cached = { key, appearance: appearanceFromState(a) };
      binding.appearanceCache.set(sessionId, cached);
    }
    return {
      actorId: a.actorId,
      label: a.label,
      countryCode: a.countryCode ? a.countryCode : null,
      appearance: cached.appearance,
      x: Number.isFinite(a.x) ? a.x : 0,
      z: Number.isFinite(a.z) ? a.z : 0,
      facing: Number.isFinite(a.facing) ? a.facing : 0,
      anim: animOf(a.anim),
      isLocal,
      posture: this.postureVisual(a),
    };
  }

  /** A seated actor's posture as drawn (the seat height from the room's shared seat list), or null while standing. */
  private postureVisual(a: ActorStateLike): ActorPostureVisual | null {
    const code = postureCode(a.posture);
    if (code === 0) return null;
    const kind = code === 2 ? "lie" : "sit";
    const seatId = typeof a.seatId === "string" ? a.seatId : "";
    const seat = seatId ? (this.deps.resolveSeat?.(seatId) ?? null) : null;
    return { kind, y: seat && seat.kind === kind ? seat.y : FALLBACK_SEAT_Y[kind], seatId };
  }

  private retireActors(old: Binding, next: Binding): void {
    // Our own actor always re-appears in the destination room (same actorId), possibly a patch
    // later than the join handshake; never remove it during a transition.
    const keep = new Set(Array.from(next.actors.values()).map((a) => a.actorId));
    keep.add(this.deps.actorId);
    for (const visual of old.actors.values()) {
      if (!keep.has(visual.actorId)) this.events.emit("actorRemove", visual.actorId);
    }
    old.actors.clear();
    old.actorVehicles.clear();
    // Cars belong to the room: those the destination does not also report leave the renderer.
    for (const id of old.vehicles.keys()) if (!next.vehicles.has(id)) this.events.emit("vehicleRemove", id);
    old.vehicles.clear();
    const local = Array.from(next.actors.values()).find((a) => a.isLocal);
    if (local) this.events.emit("localActor", local.actorId);
  }

  private dropVehicles(binding: Binding): void {
    for (const id of binding.vehicles.keys()) this.events.emit("vehicleRemove", id);
    binding.vehicles.clear();
    binding.actorVehicles.clear();
  }

  private onLeave(binding: Binding, code: number, reason?: string): void {
    if (binding.closed) return;
    binding.closed = true;
    for (const d of binding.detach) d();
    if (binding.retiring) {
      this.log(`origin room closed during transition (${code})`);
      return;
    }
    if (this.binding !== binding) return;
    const info = describeClose(code, reason);
    this.binding = null;
    for (const visual of binding.actors.values()) this.events.emit("actorRemove", visual.actorId);
    binding.actors.clear();
    this.dropVehicles(binding);
    this.events.emit("localActor", null);
    if (info.silent) {
      this.deps.storage.clear();
      this.setStatus("idle");
      return;
    }
    this.setStatus("closed");
    this.events.emit("closed", info);
    if (info.autoRejoin && this.canAutoRejoin()) this.scheduleAutoRejoin(info.state === "server_restarting" ? 2000 : 250);
  }

  /**
   * Join the last room again by itself (a restart, an expired pass, a dropped connection the SDK could not restore).
   * While the browser is offline it waits for the network first; a re-join that fails tries again, within the cap.
   */
  private scheduleAutoRejoin(delayMs: number): void {
    // Nothing left to do once the client is disposed or back in a room (the visitor pressed Rejoin meanwhile).
    const done = () => this.disposed || Boolean(this.binding);
    this.schedule(() => {
      if (done()) return;
      this.whenOnline(() => {
        if (done()) return;
        void this.rejoin().then((ok) => {
          if (!ok && !done() && this.canAutoRejoin()) this.scheduleAutoRejoin(AUTO_REJOIN_RETRY_MS);
        });
      });
    }, delayMs);
  }

  private canAutoRejoin(): boolean {
    const t = this.now();
    this.autoRejoins = this.autoRejoins.filter((x) => t - x < AUTO_REJOIN_WINDOW_MS);
    if (this.autoRejoins.length >= AUTO_REJOIN_LIMIT) return false;
    this.autoRejoins.push(t);
    return true;
  }

  private persist(binding: Binding): void {
    const token = binding.handle.reconnectionToken;
    if (!token) return;
    this.deps.storage.save({
      token,
      actorId: this.deps.actorId,
      roomName: binding.context.roomName,
      kind: binding.context.kind,
      districtId: binding.context.districtId,
      propertyId: binding.context.propertyId,
      savedAt: this.now(),
    });
  }

  private setStatus(status: ConnectionStatus, context: RoomContext | null = this.binding?.context ?? null): void {
    this.status = status;
    this.events.emit("status", status, context);
  }

  // ---------------------------------------------------------------- sending

  private canSend(): boolean {
    return Boolean(this.binding && !this.binding.closed && !this.binding.retiring && this.status === "joined");
  }

  sendInput(input: InputMove): boolean {
    if (!this.canSend()) return false;
    this.binding!.handle.send(CLIENT_MESSAGES.inputMove, input);
    return true;
  }

  /** The street, or an interior with vehicles (the truck arena): the only rooms that get driving messages. */
  private canDriveHere(): boolean {
    const ctx = this.binding!.context;
    if (ctx.kind === "street") return true;
    return ctx.propertyId !== null && (this.deps.drivableInterior?.(ctx.propertyId) ?? false);
  }

  /** Driving input for the vehicle this actor drives (street rooms and the truck arena; the server validates everything). */
  sendDrive(input: InputDrive): boolean {
    if (!this.canSend() || !this.canDriveHere()) return false;
    this.binding!.handle.send(CLIENT_MESSAGES.inputDrive, input);
    return true;
  }

  /** Get into a car / truck at its driver's door, or out of the one this actor drives. Answered by `vehicle.result`. */
  sendVehicleAction(action: VehicleAction): boolean {
    if (!this.canSend() || !this.canDriveHere() || this.transitionInFlight) return false;
    const payload: VehicleAction = action.action === "enter" && action.vehicleId ? { action: "enter", vehicleId: action.vehicleId } : { action: action.action };
    this.binding!.handle.send(CLIENT_MESSAGES.vehicleAction, payload);
    return true;
  }

  /** Attractions (ADR-0016): an intent for the venue's game (interiors only; the server decides every outcome). */
  sendVenueAction(message: VenueActionMessage): boolean {
    if (!this.canSend() || this.binding!.context.kind !== "interior" || this.transitionInFlight) return false;
    this.binding!.handle.send(CLIENT_MESSAGES.venueAction, message);
    return true;
  }

  /**
   * ADR-0053: this visitor is writing a chat line, so the AI resident talking with them waits for it instead of saying
   * goodbye. Street only, at most one every CHAT_TYPING_SEND_MS; it carries nothing and only delays a goodbye.
   */
  sendChatTyping(): boolean {
    if (!this.canSend() || this.binding!.context.kind !== "street") return false;
    const now = this.now();
    if (now - this.lastTypingSentAt < CHAT_TYPING_SEND_MS) return false;
    this.lastTypingSentAt = now;
    this.binding!.handle.send(CLIENT_MESSAGES.chatTyping, {});
    return true;
  }

  sendChat(text: string, clientMessageId: string): boolean {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > CHAT_MAX_CHARS || !this.canSend()) return false;
    this.binding!.handle.send(CLIENT_MESSAGES.chatSend, { clientMessageId, text: trimmed });
    return true;
  }

  /**
   * ADR-0043: where the Overview camera looks (ground point and the radius it shows), so the street room also sends the
   * people and cars there; `null` when the tab left Overview. Street only; the server validates and clamps it, and it
   * only widens what this tab is sent. The session throttles it (INTEREST.focusSendHz).
   */
  sendViewFocus(focus: { x: number; z: number; radius: number } | null): boolean {
    if (!this.canSend() || this.binding!.context.kind !== "street") return false;
    if (focus && !(Number.isFinite(focus.x) && Number.isFinite(focus.z) && Number.isFinite(focus.radius))) return false;
    const payload: ViewFocus = { focus: focus ? { x: focus.x, z: focus.z, radius: Math.max(0, focus.radius) } : null };
    this.binding!.handle.send(CLIENT_MESSAGES.viewFocus, payload);
    return true;
  }

  /** ADR-0043: the current room's people (visitors; AI residents excluded), from the room state, or null before it. */
  get peopleCount(): number | null {
    return this.binding && !this.binding.closed ? this.binding.peopleCount : null;
  }

  sendPresenceHint(visible: boolean): void {
    if (!this.canSend()) return;
    this.binding!.handle.send(CLIENT_MESSAGES.presenceHint, { visibilityState: visible ? "visible" : "hidden" });
  }

  /** Walk-mode Enter: the server checks the entrance radius and answers with a transition ticket. */
  requestEnter(plotId: string, expectedVersion?: number): boolean {
    if (!this.canSend() || this.binding!.context.kind !== "street" || this.transitionInFlight) return false;
    const payload: Record<string, unknown> = { action: "enter", targetId: plotId };
    if (expectedVersion !== undefined) payload["expectedVersion"] = expectedVersion;
    this.binding!.handle.send(CLIENT_MESSAGES.interaction, payload);
    this.interactionPending = true;
    return true;
  }

  requestExit(): boolean {
    if (!this.canSend() || this.binding!.context.kind !== "interior" || this.transitionInFlight) return false;
    this.binding!.handle.send(CLIENT_MESSAGES.interaction, { action: "exit" });
    this.interactionPending = true;
    return true;
  }

  /** The Enter/Exit button's request was not answered (no ticket, no failure): stop waiting for it. */
  clearPendingInteraction(): void {
    this.interactionPending = false;
    this.switchPending = false;
  }

  /**
   * ADR-0043: move to another world copy (the Worlds list: `{ copy }`) or to a friend's (`{ friendActorId }`), through the
   * CURRENT room, which reads the visitor's spot and checks everything (the copy is open and has room; for a friend,
   * their privacy setting). Answered with a `room.transition_ready` ticket (the ordinary join-then-leave) or
   * `world.switch_refused` (event `worldSwitchRefused`); a ROOM_FULL of the destination's join keeps the visitor here
   * (event `joinFailed`), never a silent move elsewhere.
   */
  requestWorldSwitch(request: WorldSwitch): boolean {
    if (!this.canSend() || this.transitionInFlight || this.switchPending) return false;
    const payload: WorldSwitch = "copy" in request ? { copy: request.copy } : { friendActorId: request.friendActorId };
    this.binding!.handle.send(CLIENT_MESSAGES.worldSwitch, payload);
    this.switchPending = true;
    this.interactionPending = true;
    return true;
  }

  /** ADR-0043: a `world.switch` is waiting for its answer. */
  get isSwitchPending(): boolean {
    return this.switchPending;
  }

  returnToPlaza(): boolean {
    if (!this.canSend() || this.binding!.context.kind !== "street") return false;
    this.binding!.handle.send(CLIENT_MESSAGES.interaction, { action: "return_to_plaza" });
    return true;
  }

  /** Sit on / lie on `seatId`, or stand up (ADR-0018). Answered by `seat.result`; the server decides everything. */
  sendSeatAction(action: SeatAction, seatId?: string): boolean {
    if (!this.canSend() || this.transitionInFlight) return false;
    const payload: Record<string, unknown> = { action };
    if (action !== "stand" && seatId) payload["targetId"] = seatId.slice(0, 64);
    this.binding!.handle.send(CLIENT_MESSAGES.interaction, payload);
    return true;
  }

  // ---------------------------------------------------------------- leaving

  /** Consented leave (navigating away). Clears the reconnection record: this is not a reload. */
  async leave(): Promise<void> {
    const binding = this.binding;
    this.binding = null;
    this.deps.storage.clear();
    if (binding && !binding.closed) {
      binding.closed = true;
      for (const d of binding.detach) d();
      for (const visual of binding.actors.values()) this.events.emit("actorRemove", visual.actorId);
      this.dropVehicles(binding);
      this.events.emit("localActor", null);
      try {
        await binding.handle.leave(true);
      } catch {
        /* already closed */
      }
    }
    this.setStatus("idle", null);
  }

  dispose(): void {
    this.disposed = true;
    void this.leave();
    this.events.clear();
  }
}

function sameTarget(a: RoomTarget | null, b: RoomTarget | null): boolean {
  if (!a || !b || a.kind !== b.kind) return false;
  // Town v6: every street target is the one street room.
  return a.kind === "street" ? true : a.propertyId === (b as { propertyId: string }).propertyId;
}

/**
 * ADR-0043: the world copy a ticket joins (a street ticket's `copy`, Robin when it names none; an interior ticket's
 * `fromCopy`, or null). The welcome confirms it.
 */
export function worldCopyOfTicket(ticket: Pick<RoomTicketResponse, "roomName" | "joinOptions">): number | null {
  if (ticket.roomName === "street") return parseCopyOption(ticket.joinOptions["copy"]) ?? 1;
  return parseCopyOption(ticket.joinOptions["fromCopy"]);
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : typeof err === "string" ? err : "";
}

function isRetryable(err: unknown): boolean {
  return Boolean(err && typeof err === "object" && "retryable" in err && (err as { retryable: boolean }).retryable);
}

/** The SDK's MatchMakeError / ServerError carry the server's numeric code; transport failures do not. */
function isServerRejection(err: unknown): boolean {
  return Boolean(err && typeof err === "object" && "code" in err && typeof (err as { code: unknown }).code === "number");
}

/** ActorState.anim as drawn: 0 idle, 1 walking, 2 running (anything else is idle). */
function animOf(v: unknown): 0 | 1 | 2 {
  return v === 2 ? 2 : v === 1 ? 1 : 0;
}

/** ActorState.posture: 0 standing, 1 sitting, 2 lying (anything else, or an older server, is standing). */
function postureCode(v: unknown): 0 | 1 | 2 {
  return v === 2 ? 2 : v === 1 ? 1 : 0;
}
